import * as PIXI from "pixi";

import { LevelData } from "piou-core/types";
import { PixelHelper } from "../utils/PixelHelper";
import { ARTWORK_MAX, FRONT_LIMIT, IRON_LIMIT } from "./conf";
import { getLine, getPlatform } from "./lib";
import Loader from "../Loader";

export default class Level extends PIXI.Container {
  renderer: PIXI.Renderer;

  lvlWidth: number;
  lvlHeight: number;

  holes: PIXI.Graphics[] = [];

  lvlBehind: PIXI.Container;
  lvlContent: PIXI.Container;
  lvlContentIron: PIXI.Container;

  ph: PixelHelper | null = null;

  //#region Build Level
  constructor(app: PIXI.Application, level: LevelData) {
    super();
    this.renderer = app.renderer as PIXI.Renderer;

    this.name = "Level";

    this.lvlWidth = level.size[0];
    this.lvlHeight = level.size[1];

    this.lvlBehind = new PIXI.Container();
    this.lvlBehind.name = "Level behind";
    this.lvlContent = new PIXI.Container();
    this.lvlContent.name = "Level content";
    this.lvlContentIron = new PIXI.Container();
    this.lvlContentIron.name = "Level iron content";

    // Build level
    this.buildTiles(level);
    this.buildPlatforms(level);
    this.buildTiles(level, true);
    this.buildOut(level);

    // Just use cacheAsBitmap
    // Convert lvl content to texture for performance
    // const lvlContentTexture = app.renderer.generateTexture(this.lvlContent, {
    //   resolution: 4,
    //   region: new PIXI.Rectangle(0, 0, this.lvlWidth, this.lvlHeight),
    // });
    // this.lvlContent.removeChildren();
    // this.lvlContent.addChild(new PIXI.Sprite(lvlContentTexture));
    // const lvlContentIronTexture = app.renderer.generateTexture(
    //   this.lvlContentIron,
    //   {
    //     resolution: 4,
    //     region: new PIXI.Rectangle(0, 0, this.lvlWidth, this.lvlHeight),
    //   }
    // );
    // this.lvlContentIron.removeChildren();
    // this.lvlContentIron.addChild(new PIXI.Sprite(lvlContentIronTexture));
    this.lvlContent.cacheAsBitmapResolution = 4;
    this.lvlContent.cacheAsBitmap = true;
    const lvlContentContaienr = new PIXI.Container();
    lvlContentContaienr.addChild(this.lvlContent);
    this.lvlContent = lvlContentContaienr;

    this.lvlContent.cacheAsBitmapResolution = 4;
    this.lvlContentIron.cacheAsBitmap = true;
    const lvlContentIronContaienr = new PIXI.Container();
    lvlContentIronContaienr.addChild(this.lvlContentIron);
    this.lvlContentIron = lvlContentIronContaienr;

    const lvlContainer = new PIXI.Container();
    lvlContainer.addChild(this.lvlContentIron, this.lvlContent);
    this.addChild(this.lvlBehind, lvlContainer);

    const f = new PIXI.filters.OutlineFilter(1, 0x000000);
    lvlContainer.filters = [f];
  }

  private buildTiles(level: LevelData, front: boolean = false) {
    for (const o of level.tiles) {
      if (front === (o.id >= FRONT_LIMIT[0] && o.id < FRONT_LIMIT[1])) {
        // var mc = new window.data.lib.mcTile();
        let mc: PIXI.DisplayObject = Loader.getSprite(
          `tile${level.did + 1}${String(o.id).padStart(4, "0")}`
        );

        mc.name = "Tile" + level.did + " " + o.id;
        mc.x = o.x;
        mc.y = o.y;
        this.lvlContent.addChild(mc);
        if (o.id >= IRON_LIMIT[0] && o.id < IRON_LIMIT[1]) {
          this.lvlContentIron.addChild(mc);
        }
      }
    }
  }

  private buildPlatforms(level: LevelData) {
    for (const o of level.platforms) {
      if (o.rid !== undefined && o.rid > ARTWORK_MAX) {
        const flGrass = true;
        const plat = getPlatform(
          this.renderer,
          o.w,
          o.rid!,
          o.rot!,
          level.did,
          flGrass
        );
        plat.name = "Platform " + o.rid;
        plat.x = o.x;
        plat.y = o.y;
        this.lvlContent.addChild(plat);
      } else if (o.list !== null) {
        const line = getLine(this.renderer, o.w, o.list!, level.did, true);
        line.name = "Line " + o.w;
        line.x = o.x;
        line.y = o.y;
        this.lvlContent.addChild(line);
      } else {
        const mc = Loader.getSprite(`mcDecor${String(o.rid).padStart(4, "0")}`);
        mc.name = "Decor " + o.rid;
        mc.scale.x = o.w / 100;
        mc.scale.y = o.w / 100;
        mc.rotation = o.rot!;
        mc.x = o.x;
        mc.y = o.y;
        this.lvlContent.addChild(mc);
      }
    }
  }

  private buildOut(level: LevelData) {
    for (const out of level.out) {
      const mc = Loader.getSprite("mcOut");
      mc.name = "mcOut";
      mc.x = out[0];
      mc.y = out[1];
      this.lvlBehind.addChild(mc);

      const outHole = new PIXI.Graphics();
      outHole.name = "outHole";
      outHole.beginFill(0xff0000).drawCircle(0, 0, 24).endFill();
      outHole.x = out[0];
      outHole.y = out[1];
      outHole.blendMode = PIXI.BLEND_MODES.DST_OUT;

      this.lvlContentIron.addChild(outHole);
      this.lvlContent.addChild(outHole);
    }
  }
  //#endregion

  makeCustomHole(
    x: number,
    y: number,
    graphics: PIXI.Graphics,
    onIron?: boolean
  ) {
    const hole = graphics;
    hole.name = "hole"; // To clean level, remove all "hole" graphics TODO: better way to identify holes ?
    hole.x = x;
    hole.y = y;
    hole.blendMode = PIXI.BLEND_MODES.DST_OUT;

    this.lvlContent.addChild(hole);
    if (onIron) {
      this.lvlContentIron.addChild(hole);
    }

    this.invalidateCache();
  }

  makeHole(x: number, y: number, radius: number, onIron?: boolean) {
    const hole = new PIXI.Graphics();
    hole.beginFill(0xff0000).drawCircle(0, 0, radius).endFill();
    return this.makeCustomHole(x, y, hole, onIron);
  }

  cleanLevel() {
    const toRemove: PIXI.DisplayObject[] = [];
    for (const child of this.lvlContent.children)
      if (child.name === "hole") toRemove.push(child);
    this.lvlContent.removeChild(...toRemove);
    toRemove.length = 0;

    for (const child of this.lvlContentIron.children)
      if (child.name === "hole") toRemove.push(child);
    this.lvlContentIron.removeChild(...toRemove);

    this.invalidateCache();
  }

  isFree(x: number, y: number): boolean {
    if (this.ph === null) {
      this.ph = new PixelHelper(
        this.renderer,
        this.lvlContent,
        new PIXI.Rectangle(0, 0, this.lvlWidth, this.lvlHeight)
      );
    }

    return !this.ph.hitTest(new PIXI.Point(x, y));
  }

  private invalidateCache() {
    this.ph?.invalidate();
  }
}
